import { EntityType, useAppSelector } from '@frontend/common';
import { Form, SubFormProps } from '@frontend/components/forms';
import { EnumSelectListV2 } from '@frontend/components/select';
import { useModals } from '@frontend/modal';
import { formStore } from '@frontend/repository';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import OverviewHeader from '../../applications/header/overview-header';
import { useAppDispatch } from '../../redux/store';

export interface CreateModalProps<T, S> {
    initialValue?: S;
    order: (keyof S | Array<keyof S>)[];
    required?: (keyof S | Array<keyof S>)[];
    onSubmit: (value: S) => Promise<T>;
    additionalSubmit?: {
        label: React.ReactNode;
        onClick: (value: S) => Promise<T | void>;
        onSubmitSuccess?: (value: T) => void;
        onVoidSuccess?: (value: void) => void;
    };
    successMessageOverwrite?: React.ReactNode;
    expectedType?: Map<keyof S, 'object' | 'boolean' | 'number' | 'string' | 'entity' | 'textarea'>;
    fieldProps?: (formValues: { [key: string]: any }) => Map<keyof S, any>;
    formId?: string;
    formIdCallback?: (id: string) => void;
    submitDisabled?: (formValues: { [key: string]: any }) => boolean;
    submitButtonOverride?: React.ReactNode;
    append?: React.ReactNode;
    type: EntityType;
    title?: React.ReactNode;
    label?: React.ReactNode;
    keyOverwrite?: Map<keyof S, React.ReactNode>;
    subForms?: Map<React.ReactNode, SubFormProps<any, any>>;
    fieldOverwrite?: Map<keyof S, React.ReactNode>;
    enumOverwrite?: Map<keyof S, keyof typeof EnumSelectListV2>;
    styles?: {
        submitButton?: string;
        cancelButton?: string;
        body?: string;
    };
    footerButton?: React.ReactNode;
    onSubmitSuccess?: (value: T) => void;
}

const CreateModal = <T extends object, S extends object>(props: CreateModalProps<T, S>) => {
    const { closeTopModal } = useModals();
    const [value, setValue] = useState<S>(props.initialValue ?? ({} as S));
    const formId = props.formId ?? crypto.randomUUID();

    const form = useAppSelector(useSelector, formStore);
    const formValues = useMemo(() => {
        const f = form[formId];
        if (!f) return {};
        return f.value;
    }, [form, formId]);

    useEffect(() => {
        if (props.formIdCallback && formId) {
            props.formIdCallback(formId);
        }
    }, [props.formIdCallback, formId]);

    return (
        <Form<T, S>
            {...props}
            dispatch={useAppDispatch()}
            type={props.type}
            value={value}
            onChange={setValue}
            show={props.order}
            required={props.required}
            bodyClassname={`modal bg-background-default p-6 h-full gap-6 flex flex-col ${props.styles?.body ?? ''}`}
            headerClassName='modal-header px-0 py-0'
            cardBodyClassName='self-stretch p-4 bg-background-surface rounded-xl border inline-flex flex-col justify-start items-start border-border-default'
            footerClassName='self-stretch inline-flex justify-between gap-3 items-center'
            label={
                <OverviewHeader
                    onBack={closeTopModal}
                    title={props.label}
                />
            }
            onSubmit={props.onSubmit}
            onSubmitSuccess={(v) => {
                if (props.onSubmitSuccess) {
                    props.onSubmitSuccess(v);
                }
                closeTopModal();
            }}
            expectedType={props.expectedType}
            fieldProps={props.fieldProps ? props.fieldProps(formValues) : undefined}
            id={formId}
            useStaticId
            submitDisabled={props.submitDisabled ? props.submitDisabled(formValues) : false}
            submitButtonOverride={props.submitButtonOverride}
            append={props.append}
            footerButton={props.footerButton}
        />
    );
};

export default CreateModal;
